import { ABILITIES, ABILITY_NAMES, SKILLS, SKILL_KEYS, type Ability, type SkillKey } from '@shared/rules/constants'
import { deriveStats } from '@shared/rules/calc'
import type { BuiltCharacter, CharacterDraft } from '@shared/rules/build'
import { resolveContext } from '@shared/rules/build'
import { SPELLS_BY_ID } from '@shared/rules/data/spells'
import { FULL_CASTER_SLOTS, PACT_SLOTS } from '@shared/rules/constants'
import type { SheetData, SheetSpell } from './sheetData'

/** Level-1 slot totals for the class being built. */
function slotsForLevel1(casting: 'full' | 'half' | 'third' | 'pact' | 'none'): Record<number, number> {
  if (casting === 'none') return {}
  if (casting === 'pact') {
    const entry = PACT_SLOTS[0]!
    return { [entry[1]]: entry[0] }
  }
  // Half and third casters reach caster level 1 at character level 1 in 2024.
  const row = FULL_CASTER_SLOTS[0]!
  const slots: Record<number, number> = {}
  row.forEach((count, i) => { if (count > 0) slots[i + 1] = count })
  return slots
}

function toSheetSpell(id: string): SheetSpell | null {
  const spell = SPELLS_BY_ID.get(id)
  if (!spell) return null
  return {
    level: spell.level,
    name: spell.name,
    castingTime: spell.castingTime,
    range: spell.range,
    concentration: spell.concentration,
    ritual: spell.ritual,
    material: spell.material,
    // The Notes column fits roughly 28 characters, so it carries duration —
    // the one field the sheet's own columns leave out.
    notes: spell.duration,
  }
}

export function draftToSheetData(draft: CharacterDraft, built: BuiltCharacter): SheetData {
  const ctx = resolveContext(draft)
  if (!ctx) throw new Error('Draft is incomplete')
  const { classBuild, species, background } = ctx

  // Reuse the same derived-stat engine the app and API use, so a printed
  // bonus always matches the one on screen.
  const derived = deriveStats({
    level: 1,
    class: classBuild.id,
    scores: built.scores,
    saveProficiencies: built.saveProficiencies,
    skillProficiencies: built.skillProficiencies,
    armorClass: built.armorClass,
    acIsManual: true,
    initiativeBonus: built.initiativeBonus,
  })

  // The Speed box is only wide enough for about a dozen characters, so the
  // unit is dropped once there is a second movement mode to show.
  const extraSpeeds = Object.entries(built.otherSpeeds)
  const speed = extraSpeeds.length
    ? [String(built.speed), ...extraSpeeds.map(([mode, value]) => `${mode} ${value}`)].join(' · ')
    : `${built.speed} ft`

  // Magic Initiate spells belong on the sheet even for a class that casts
  // nothing, so they are merged in and tagged with where they came from.
  const classSpells = [...draft.cantrips, ...draft.spells]
    .map(toSheetSpell)
    .filter((s): s is SheetSpell => s !== null)

  const featSpellRows: SheetSpell[] = []
  for (const grant of built.featSpells) {
    for (const id of [...grant.cantrips, grant.spell].filter(Boolean)) {
      const row = toSheetSpell(id)
      if (row) featSpellRows.push({ ...row, notes: `${row.notes} · ${grant.featName}` })
    }
  }

  const spells = [...classSpells, ...featSpellRows]
    .sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))

  const backstoryParts = [
    draft.personality && `Personality: ${draft.personality}`,
    draft.backstory,
    draft.lostSomething && `What the Hourglass Coven took from me: ${draft.lostSomething}`,
  ].filter(Boolean)

  return {
    name: draft.name,
    className: classBuild.name,
    subclass: '',
    species: species.name,
    background: background.name,
    level: 1,
    xp: 0,

    size: built.size.replace(' or Medium', '/M').replace('Small', 'S').replace('Medium', 'M'),
    speed,
    armorClass: built.armorClass,
    shieldEquipped: built.acSource.includes('Shield'),

    hpMax: built.hpMax,
    hpCurrent: built.hpMax,
    hpTemp: 0,
    hitDiceMax: `1d${built.hitDie}`,
    hitDiceSpent: 0,

    proficiencyBonus: built.proficiencyBonus,
    initiative: derived.initiative,
    passivePerception: derived.passivePerception,
    heroicInspiration: false,

    scores: built.scores,
    modifiers: built.modifiers,
    saveProficiencies: built.saveProficiencies,
    saveBonuses: Object.fromEntries(
      ABILITIES.map((a) => [a, derived.saves[a]]),
    ) as Record<Ability, number>,
    skillProficiencies: built.skillProficiencies,
    skillBonuses: Object.fromEntries(
      SKILL_KEYS.map((s) => [s, derived.skills[s]]),
    ) as Record<SkillKey, number>,

    attacks: built.attacks,
    classFeatures: built.classFeatures,
    speciesTraits: built.speciesTraits,
    feats: built.feats,

    armorTraining: built.armorTraining,
    weaponTraining: built.weaponTraining,
    tools: built.toolTraining,
    languages: built.languages,
    equipment: built.equipment,
    coins: built.coins,

    alignment: draft.alignment,
    appearance: draft.appearance,
    backstory: backstoryParts.join('\n\n'),

    spellcasting: buildSpellcastingBlock(built, classBuild.id, spells),
  }
}

/**
 * Uses the class's own spellcasting when it has any, and otherwise falls back
 * to a Magic Initiate feat so those spells still get a save DC on the sheet.
 */
function buildSpellcastingBlock(
  built: BuiltCharacter,
  classId: string,
  spells: SheetSpell[],
): SheetData['spellcasting'] {
  if (built.spellcasting) {
    return {
      abilityName: ABILITY_NAMES[built.spellcasting.ability],
      modifier: built.spellcasting.modifier,
      saveDC: built.spellcasting.saveDC,
      attackBonus: built.spellcasting.attackBonus,
      slots: slotsForLevel1(castingKind(classId)),
      spells,
    }
  }

  const grant = built.featSpells[0]
  if (!grant) return null

  return {
    abilityName: ABILITY_NAMES[grant.ability],
    modifier: built.modifiers[grant.ability],
    saveDC: grant.saveDC,
    attackBonus: grant.attackBonus,
    slots: {},
    spells,
  }
}

function castingKind(classId: string): 'full' | 'half' | 'third' | 'pact' | 'none' {
  if (classId === 'warlock') return 'pact'
  if (classId === 'paladin' || classId === 'ranger') return 'half'
  if (['bard', 'cleric', 'druid', 'sorcerer', 'wizard'].includes(classId)) return 'full'
  return 'none'
}

export { SKILLS }
